import { useState, type MouseEvent } from "react";
import { categories, stories, type Story } from "@/mocks/stories";
import Reveal from "@/components/base/Reveal";

interface FeedProps {
  activeFilter: string;
  onSetFilter: (key: string) => void;
  onOpenStory: (story: Story) => void;
}

const filters = [{ key: "all", label: "全部爆梗", emoji: "🍿" }, ...categories];

export default function Feed({ activeFilter, onSetFilter, onOpenStory }: FeedProps) {
  const [liked, setLiked] = useState<Record<number, boolean>>({});
  const list = activeFilter === "all" ? stories : stories.filter((s) => s.cat === activeFilter);

  const toggleLike = (id: number, e: MouseEvent) => {
    e.stopPropagation();
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="feed" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1240px] px-4 md:px-6">
        <Reveal>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-800">
                <i className="ri-emotion-laugh-fill"></i> 栏目·今日爆梗
              </span>
              <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">别人的社死，我们的快乐</h2>
              <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
                每天从上千条投稿里挑出最离谱的桥段，配图吐槽一条龙。挑个口味，开吃！
              </p>
            </div>
          </div>
        </Reveal>

        {/* filter chips */}
        <Reveal>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => onSetFilter(f.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeFilter === f.key
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <span aria-hidden="true">{f.emoji}</span>
                {f.label}
                {activeFilter === f.key && <i className="ri-check-line"></i>}
              </button>
            ))}
          </div>
        </Reveal>

        {/* grid */}
        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-3xl bg-background-100 px-6 py-16 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-background-200 text-foreground-400">
              <i className="ri-archive-line text-2xl"></i>
            </span>
            <p className="font-medium text-foreground-800">这个口味还在翻找中</p>
            <p className="text-sm text-foreground-500">换个分类，快乐立刻续上～</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((s, i) => (
              <Reveal key={s.id} delay={i % 3 === 0 ? 0 : i % 3 === 1 ? 60 : 120}>
                <article
                  className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl bg-background-100 transition-transform duration-200 hover:-translate-y-1"
                  onClick={() => onOpenStory(s)}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.title}的搞笑配图`}
                      className="h-44 w-full object-cover object-top transition-transform duration-300 group-hover:scale-105 md:h-48"
                    />
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-semibold text-primary-700">
                      <span aria-hidden="true">{categories.find((c) => c.key === s.cat)?.emoji}</span>
                      {s.tag}
                    </span>
                    <span className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-primary-500 text-background-50 opacity-0 transition-opacity group-hover:opacity-100">
                      <i className="ri-arrow-right-up-line"></i>
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-4 md:p-5">
                    <h3 className="line-clamp-2 text-base font-bold leading-snug text-foreground-950 md:text-lg">
                      {s.title}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{s.snippet}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3.5 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {s.reads} 围观
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {s.date}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary-700">
                        <i className="ri-arrow-right-circle-line"></i> 点我看完整现场
                      </span>
                      <button
                        onClick={(e) => toggleLike(s.id, e)}
                        aria-label="笑一个"
                        className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                          liked[s.id] ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-600 hover:bg-background-200"
                        }`}
                      >
                        <i className="ri-emotion-laugh-fill"></i>
                        {s.likes + (liked[s.id] ? 1 : 0)}
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}